
import Vue from "vue";
import { Component, Provide, Prop } from "vue-property-decorator";
import ControlsTree from "@/components/Reusable/ControlsTree/ControlsTree.vue";
import SearchBox, { SearchBoxState } from "./SearchBox.vue";
import { uiStore, configStore, documentStore } from "@/store/store";
import {
  Control,
  ControlsTreeContext,
  ChangeControlValueMeta
} from "@/types/controls";
import lodash from "lodash";
import DesignContentSettingsTabs from "./DesignContentSettingsTabs.vue";
import DesignTabContent from "./DesignTabContent.vue";
import { filterControlsThatCanBeModified } from "@/lib/permissions/utils";
import { getPathWithoutRepeaterIndex } from "@/lib/dynamic-data/utils";
import {
  filterBuilderElementControls,
  flattenAndCount
} from "@/lib/controls/search";
import {
  getElementAccordionState,
  setElementAccordionState
} from "@/lib/panels/accordions";
import {
  separatePathPrefixFromElementControlPath,
  getElementControlsListBasedOnPathPrefix
} from "@/lib/elements/utils";
import { addMetadataToDocumentPropertyChangeBasingOnControlValueChangeMetadata } from "@/lib/controls/utils";
import Icon from "@/components/UI/Icon.vue";
import SearchBoxEmptyState from "@/components/ElementPropertiesPanel/SearchBoxEmptyState.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import ElementPresetChooser from "@/components/ElementPropertiesPanel/ElementPresetChooser.vue";
import { doesElementHavePreset } from "@/lib/presets/presets";

@Component({
  components: {
    ElementPresetChooser,
    DropdownInput,
    ControlsTree,
    SearchBox,
    DesignContentSettingsTabs,
    DesignTabContent,
    Icon,
    SearchBoxEmptyState
  }
})
export default class TabsAndControls extends Vue {
  @Prop()
  value!: boolean;

  searchBoxState: SearchBoxState = {
    modifiedOnly: false,
    includeSettings: false,
    query: ""
  };

  get element() {
    return uiStore.activeElement;
  }

  get elementId() {
    return uiStore.activeElementId;
  }

  clearQuery() {
    this.updateSearchBoxState({
      modifiedOnly: false,
      query: "",
      includeSettings: false
    });
  }

  updateSearchBoxState(newSearchBoxState: SearchBoxState) {
    this.searchBoxState = newSearchBoxState;
  }

  get hasSearchParameters() {
    return this.searchBoxState.modifiedOnly || this.searchBoxState.query;
  }

  get noSearchResults() {
    return (
      flattenAndCount(this.controlsFilteredBySearchParams.contentSections, 0) +
        flattenAndCount(this.controlsFilteredBySearchParams.designSections, 0) +
        flattenAndCount(
          this.controlsFilteredBySearchParams.settingsSections,
          0
        ) ===
      0
    );
  }

  get controlsFilteredBySearchParams() {
    const controls = this.searchBoxState.includeSettings
      ? {
          designSections: [],
          contentSections: [],
          settingsSections: this.controls.settingsSections
        }
      : {
          ...this.controls,
          settingsSections: []
        };

    // Hide design controls if preset is selected
    if (this.isPresetEnabled && !this.isPresetsOverridesEnabled) {
      return {
        ...controls,
        designSections: []
      };
    }

    return filterBuilderElementControls(
      this.searchBoxState.modifiedOnly ? this.controls : controls,
      this.searchBoxState.modifiedOnly,
      this.searchBoxState.query,
      this.editingElementControlsTreeContext
    );
  }

  @Provide("context")
  editingElementControlsTreeContext = editingElementControlsTreeContext;

  get controls() {
    // @ts-ignore
    const type = this.element.data.type;
    const controls = configStore.element(type).controls;

    // for client mode / permissions
    return filterControlsThatCanBeModified(controls, configStore.permission);
  }

  get isPresetEnabled() {
    if (!this.element) return false;
    return doesElementHavePreset(this.element.id);
  }

  get searchDisabled() {
    return (
      this.tab === "design" &&
      this.isPresetEnabled &&
      !this.isPresetsOverridesEnabled
    );
  }

  get isPresetsOverridesEnabled() {
    return configStore.isExperimentEnabled("design-presets-overrides");
  }

  get contentSections() {
    return this.controls.contentSections;
  }

  get designSections() {
    return this.controls.designSections;
  }

  get settingsSections() {
    return this.controls.settingsSections;
  }

  get hasContentControls() {
    return this.controls.contentSections.length > 0;
  }

  get hasDesignControls() {
    return this.controls.designSections.length > 0;
  }

  get hasSettingsControls() {
    return this.controls.settingsSections.length > 0;
  }

  get tab() {
    if (
      this.elementId &&
      uiStore.panelState.elementPropertiesPanelActiveTab[this.elementId]
    ) {
      return uiStore.panelState.elementPropertiesPanelActiveTab[this.elementId];
    }

    if (this.hasContentControls) return "content";
    if (this.hasDesignControls) return "design";
    if (this.hasSettingsControls) return "settings";

    return "";
  }

  updateTab(tab: "content" | "design" | "settings" | undefined) {
    if (!this.elementId) return;

    uiStore.setPanelState({
      ...uiStore.panelState,
      elementPropertiesPanelActiveTab: {
        ...uiStore.panelState.elementPropertiesPanelActiveTab,
        [this.elementId]: tab
      }
    });
  }

  searchString = "";
}

const editingElementControlsTreeContext: ControlsTreeContext = {
  dynamicProperty: (controlPath: string) => {
    if (!uiStore.activeElement) return false;

    const dynamicPropertyPaths = configStore.element(
      uiStore.activeElement.data.type
    ).dynamicPropertyPaths;

    if (!dynamicPropertyPaths) return false;

    const controlPathWithoutRepeaterIndexAndBreakpoint =
      getPathWithoutRepeaterIndex(controlPath)
        /**
         *  Remove breakpoint for matching against the dynamicPropertyPath
         */
        .replace(`.${uiStore.currentBreakpoint.id}`, "");

    const dynPropPathThatMatchesControlPath = dynamicPropertyPaths.find(
      dynPropPath =>
        getPathWithoutRepeaterIndex(dynPropPath.path) ===
        controlPathWithoutRepeaterIndexAndBreakpoint
    );

    return dynPropPathThatMatchesControlPath?.accepts
      ? dynPropPathThatMatchesControlPath
      : false;
  },

  getControlValue: controlPath => {
    return lodash.get(uiStore.activeElement, `data.properties.${controlPath}`);
  },
  setControlValue(
    controlPathProbablyWithBreakpointIdAndHoverSuffix,
    newValue,
    control,
    meta?: ChangeControlValueMeta
  ) {
    if (!uiStore.activeElementId) return;

    documentStore.throttledPropertyChanged(
      addMetadataToDocumentPropertyChangeBasingOnControlValueChangeMetadata(
        {
          elementId: uiStore.activeElementId,
          path: controlPathProbablyWithBreakpointIdAndHoverSuffix,
          value: newValue
        },
        {
          control,
          controlsTree:
            this.getControls(
              controlPathProbablyWithBreakpointIdAndHoverSuffix
            ) || []
        },
        meta
      )
    );
  },
  getControls: (controlPath: string): Control[] | undefined => {
    if (!uiStore.activeElement) {
      return undefined;
    }

    const splittedPath = separatePathPrefixFromElementControlPath(controlPath);

    if (splittedPath) {
      return getElementControlsListBasedOnPathPrefix(
        configStore.element(uiStore.activeElement.data.type),
        splittedPath.prefix
      );
    }

    return undefined;
  },
  setAccordionState: (accordionPath: string, open: boolean) => {
    if (!uiStore.activeElementId) return;
    setElementAccordionState(uiStore.activeElementId, accordionPath, open);
  },
  getAccordionState: (accordionPath: string) => {
    if (!uiStore.activeElementId) return false;
    return getElementAccordionState(uiStore.activeElementId, accordionPath);
  }
};
